import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import {
  SectionHeading, SignalCard, EmptyState, NNSelect, Skeleton,
  FilterBar, FilterSearch,
  Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Tooltip, TooltipTrigger, TooltipContent, ICON_PX, useToast, Input,
} from '../ui';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';
import { useAuth } from '../../contexts/AuthContext';
import { fetchConsumerSignals, addToPriorityQueue, dismissConsumerSignal } from '../../utils/api';

const URGENCY_OPTIONS = ['all', '5', '4', '3'];
const CATEGORY_OPTIONS = ['all', 'legal_escalation', 'billing_dispute', 'review_alert', 'patient_complaint', 'cahps_notification'];

function ConsumerSignalFeedTab({ onCreateComplaint, readOnly }) {
  const { selectedUnitId } = useConsumerUnit();
  const { getToken } = useAuth();
  const [allSignals, setAllSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const queuedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchConsumerSignals(selectedUnitId, getToken).then((res) => {
      if (!cancelled) { setAllSignals(Array.isArray(res) ? res : []); setLoading(false); }
    }).catch(() => {
      if (!cancelled) { console.warn('[ConsumerSignalFeedTab] API fetch failed'); setAllSignals([]); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [selectedUnitId, getToken]);

  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dismissed, setDismissed] = useState(new Set());
  const [dismissDialog, setDismissDialog] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [draftDialog, setDraftDialog] = useState(null);
  const [draftCategory, setDraftCategory] = useState('patient_complaint');
  const [draftUrgency, setDraftUrgency] = useState('3');
  const [draftPatient, setDraftPatient] = useState('');

  // Priority Queue: auto-promote legal signals
  useEffect(() => {
    if (readOnly || queuedRef.current || loading) return;
    const legal = allSignals.filter((s) => s.isLegal && !dismissed.has(s.id));
    if (legal.length === 0) return;
    queuedRef.current = true;
    legal.forEach((s) => {
      addToPriorityQueue('all', {
        title: `Legal Signal: ${s.title}`,
        priority: 'critical', category: 'Consumer', type: 'task',
        estimatedTime: 45, dueDate: new Date().toLocaleDateString('en-CA'),
      }, getToken).catch(() => {});
    });
  }, [allSignals, readOnly, dismissed, loading, getToken]);

  const filtered = useMemo(() => {
    let list = allSignals.filter((s) => !dismissed.has(s.id));
    if (urgencyFilter !== 'all') list = list.filter((s) => s.urgency >= Number(urgencyFilter));
    if (categoryFilter !== 'all') list = list.filter((s) => s.classification === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.title.toLowerCase().includes(q) || s.sender?.toLowerCase().includes(q));
    }
    return list;
  }, [allSignals, dismissed, urgencyFilter, categoryFilter, search]);

  const pinned = filtered.filter((s) => s.isLegal);
  const unpinned = filtered.filter((s) => !s.isLegal);

  const handleDismiss = () => {
    if (!dismissDialog) return;
    const id = dismissDialog.id;
    const reason = dismissReason || 'No reason';
    dismissConsumerSignal(id, getToken)
      .then(() => {
        setDismissed((prev) => new Set(prev).add(id));
        toast({ title: `Signal dismissed: ${reason}` });
      })
      .catch(() => toast({ title: 'Failed to dismiss signal', variant: 'destructive' }));
    setDismissDialog(null);
    setDismissReason('');
  };

  const openDraftDialog = (signal) => {
    setDraftDialog(signal);
    setDraftCategory(signal.classification === 'legal_escalation' ? 'legal_escalation' : 'patient_complaint');
    setDraftUrgency(String(signal.urgency));
    setDraftPatient(signal.entities?.patient || '');
  };

  const confirmComplaint = () => {
    if (!draftDialog) return;
    onCreateComplaint?.({
      ...draftDialog,
      category: draftCategory,
      urgency: Number(draftUrgency),
      patientContact: draftPatient,
    });
    toast({ title: 'Complaint created from signal' });
    setDraftDialog(null);
  };

  const handleQueue = (signalId) => {
    const sig = allSignals.find((s) => s.id === signalId);
    if (!sig) return;
    addToPriorityQueue('all', {
      title: sig.title,
      priority: sig.priority, category: 'Consumer', type: 'task',
      estimatedTime: 30, dueDate: new Date().toLocaleDateString('en-CA'),
    }, getToken).catch(() => {});
    toast({ title: 'Added to Priority Queue' });
  };

  const renderSignalActions = readOnly ? {} : {
    onQueue: handleQueue,
    onDismiss: (id) => { setDismissDialog(allSignals.find((s) => s.id === id)); setDismissReason(''); },
  };

  if (loading) return <Skeleton className="h-[200px] w-full rounded-lg" />;

  return (
    <div className="flex flex-col gap-3">
      <SectionHeading
        size="sm" className="mb-1.5" title="Consumer Signal Feed"
        action={
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
            </TooltipTrigger>
            <TooltipContent side="bottom" maxWidth={220}>Consumer-domain email signals from NX-003 classification. Legal escalations pinned at top.</TooltipContent>
          </Tooltip>
        }
      />

      <FilterBar>
        <FilterSearch value={search} onChange={setSearch} placeholder="Search..." />
        <div className="flex items-center gap-2">
          <NNSelect value={urgencyFilter} onChange={setUrgencyFilter} options={URGENCY_OPTIONS.map((u) => ({ value: u, label: u === 'all' ? 'All Urgency' : `>= ${u}` }))} />
          <NNSelect value={categoryFilter} onChange={setCategoryFilter} options={CATEGORY_OPTIONS.map((c) => ({ value: c, label: c === 'all' ? 'All Categories' : c.replace(/_/g, ' ') }))} />
        </div>
      </FilterBar>

      {/* Pinned legal signals */}
      {pinned.length > 0 && (
        <div>
          <SectionHeading
            size="sm" className="mb-1.5" title="Legal Escalations (Pinned)"
            action={
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="flex items-center text-text-muted hover:text-text-dark-secondary transition-colors"><Info size={ICON_PX.md} /></button>
                </TooltipTrigger>
                <TooltipContent side="bottom" maxWidth={220}>Urgency 5 signals auto-pinned and auto-promoted to Priority Queue.</TooltipContent>
              </Tooltip>
            }
          />
          <div className="flex flex-col gap-2">
            {pinned.map((sig) => (
              <div key={sig.id} className="relative">
                <SignalCard signal={sig} density="default" {...renderSignalActions} />
                {!readOnly && (
                  <Button variant="default" size="sm" className="absolute top-2 right-2" onClick={() => openDraftDialog(sig)}>
                    Create Complaint
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regular signals */}
      {unpinned.length === 0 && pinned.length === 0 ? (
        <EmptyState title="No signals" body="No consumer signals match the current filters." />
      ) : (
        <div className="flex flex-col gap-2">
          {unpinned.map((sig) => (
            <div key={sig.id} className="relative">
              <SignalCard signal={sig} density="default" {...renderSignalActions} />
              {!readOnly && (
                <Button variant="default" size="sm" className="absolute top-2 right-2" onClick={() => openDraftDialog(sig)}>
                  Create Complaint
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dismiss Dialog */}
      <Dialog open={!!dismissDialog} onOpenChange={(open) => !open && setDismissDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dismiss Signal</DialogTitle></DialogHeader>
          <p className="text-body-sm text-text-muted mb-2">{dismissDialog?.title}</p>
          <Input value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} placeholder="Reason for dismissal..." />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDismissDialog(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDismiss}>Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Draft Complaint Dialog (pre-filled from signal) */}
      <Dialog open={!!draftDialog} onOpenChange={(open) => !open && setDraftDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Complaint from Signal</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-caption font-bold text-text-muted">Subject</label>
              <p className="text-body text-text-dark">{draftDialog?.title}</p>
            </div>
            <div>
              <label className="text-caption font-bold text-text-muted">Category</label>
              <NNSelect value={draftCategory} onChange={setDraftCategory} options={[
                { value: 'patient_complaint', label: 'Patient Complaint' },
                { value: 'formal_grievance', label: 'Formal Grievance' },
                { value: 'legal_escalation', label: 'Legal Escalation' },
              ]} />
            </div>
            <div>
              <label className="text-caption font-bold text-text-muted">Urgency</label>
              <NNSelect value={draftUrgency} onChange={setDraftUrgency} options={[1,2,3,4,5].map((u) => ({ value: String(u), label: `Urgency ${u}${u === 5 ? ' (Legal)' : ''}` }))} />
            </div>
            <div>
              <label className="text-caption font-bold text-text-muted">Patient Contact (if known)</label>
              <Input value={draftPatient} onChange={(e) => setDraftPatient(e.target.value)} placeholder="Patient name or contact..." />
            </div>
            <p className="text-caption text-text-muted">Source: Email Signal ({draftDialog?.id})</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDraftDialog(null)}>Cancel</Button>
            <Button variant="success" onClick={confirmComplaint}>Confirm & Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ConsumerSignalFeedTab;
